import { postRequestOptions } from './Fetchwrapper';

/** One cell as the Excel export endpoint takes it: text, spans, header / bold, alignment. */
export interface ExportCell {
	v: string;
	cs?: number;
	rs?: number;
	h?: 1;
	b?: 1;
	a?: 'left' | 'center' | 'right';
}

export interface ExportSheet {
	name: string;
	title: string;
	headerRows: number;
	rows: ExportCell[][];
}

export interface ExcelExportOptions {
	/** Report name: the first line of every sheet. */
	title: string;
	/** File name without .xlsx. */
	filename: string;
	/** Filter lines under the title, e.g. "Project: Lubricants", "From 2026-09-01 to 2026-10-17". */
	meta?: string[];
	/** Sheets made from the report's data, put before its tables (for figures that are not in a table). */
	extraSheets?: ExportSheet[];
}

/** Filter lines for the top of the sheet ("Project: Lubricants"); filters left empty are not listed. */
export function reportMeta(filters: Record<string, string | number | null | undefined>): string[] {
	return Object.entries(filters)
		.filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
		.map(([label, value]) => `${label}: ${value}`);
}

/** File name from the report name and its dates: "daily-sales-report_2026-09-01_2026-10-17". */
export function reportFileName(name: string, ...parts: Array<string | null | undefined>): string {
	return [name, ...parts.filter((part) => part && String(part).trim() !== '')].join('_');
}

// A select's label, or '' while nothing is chosen ("Select Project", value '').
export const chosen = (option?: { label?: any; value?: any } | null) => (option && option.value !== '' && option.value !== null && option.value !== undefined ? String(option.label) : '');

const HEADING ='h1,h2,h3,h4,h5,h6,.card-title,.card-label';
const FORM_CONTROL = 'input,select,textarea';
// Inside a cell: what is not report data (buttons, icons, filter boxes) is left out of the text.
const NOT_DATA = 'button,svg,i.fa,i.fas,i.far,i.bi,.no-export,[data-export-ignore],' + FORM_CONTROL;

const clean = (text: string) => text.replace(/\s+/g, ' ').trim();

function cellText(cell: HTMLElement): string {
	const copy = cell.cloneNode(true) as HTMLElement;
	copy.querySelectorAll(NOT_DATA).forEach((element) => element.remove());
	return clean(copy.textContent || '');
}

function cellAlign(cell: HTMLElement): ExportCell['a'] {
	const classes = cell.classList;
	if (classes.contains('text-end') || classes.contains('text-right')) return 'right';
	if (classes.contains('text-center')) return 'center';
	if (classes.contains('text-start') || classes.contains('text-left')) return 'left';
	const align = cell.style.textAlign || window.getComputedStyle(cell).textAlign;
	if (align === 'right' || align === 'end') return 'right';
	if (align === 'center') return 'center';
	return undefined;
}

function isBold(cell: HTMLElement): boolean {
	const boldClass = (element: Element | null) => !!element && /\bfw-(bold|bolder|semibold)\b/.test(element.className || '');
	if (boldClass(cell) || boldClass(cell.parentElement)) return true;
	const text = cellText(cell);
	if (text === '') return false;
	// The whole text in <strong>/<b> (e.g. "<strong>Grand Total:</strong>").
	const strong = Array.from(cell.querySelectorAll('strong,b,.fw-bold,.fw-bolder'))
		.map((element) => clean(element.textContent || ''))
		.join(' ');
	return clean(strong) === text;
}

function isHidden(element: HTMLElement): boolean {
	return element.hidden || element.style.display === 'none' || window.getComputedStyle(element).display === 'none';
}

// A filter row: search boxes and no text of its own.
function isFilterRow(row: HTMLTableRowElement): boolean {
	if (!row.querySelector(FORM_CONTROL)) return false;
	return Array.from(row.cells).every((cell) => cellText(cell) === '');
}

function tableRows(table: HTMLTableElement): { rows: ExportCell[][]; headerRows: number } {
	const rows: ExportCell[][] = [];
	let headerRows = 0;

	Array.from(table.rows).forEach((row) => {
		if (row.closest('table') !== table || isHidden(row) || row.hasAttribute('data-export-ignore') || isFilterRow(row)) {
			return;
		}
		const inHead = row.parentElement?.tagName === 'THEAD';
		const cells = Array.from(row.cells).map((cell) => {
			const exportCell: ExportCell = { v: cellText(cell) };
			if (cell.colSpan > 1) exportCell.cs = cell.colSpan;
			if (cell.rowSpan > 1) exportCell.rs = cell.rowSpan;
			if (inHead) exportCell.h = 1;
			else if (cell.tagName === 'TH' || isBold(cell)) exportCell.b = 1;
			const align = cellAlign(cell);
			if (align) exportCell.a = align;
			return exportCell;
		});
		rows.push(cells);
		if (inHead) headerRows = rows.length;
	});

	return { rows, headerRows };
}

// The heading nearest above the table inside the report (a card title, an h5...), or ''.
function tableTitle(table: HTMLTableElement, container: HTMLElement): string {
	const explicit = table.closest('[data-export-title]');
	if (explicit && container.contains(explicit)) {
		return explicit.getAttribute('data-export-title') || '';
	}

	let node: Element | null = table;
	while (node && node !== container) {
		let sibling = node.previousElementSibling;
		while (sibling) {
			if (sibling.matches(HEADING)) return clean(sibling.textContent || '');
			if (!sibling.querySelector('table')) {
				const headings = sibling.querySelectorAll(HEADING);
				if (headings.length) return clean(headings[headings.length - 1].textContent || '');
			}
			sibling = sibling.previousElementSibling;
		}
		node = node.parentElement;
	}
	return '';
}

/** The report's tables as the screen shows them, one sheet each; nested and hidden tables are left out. */
export function readTables(container: HTMLElement): ExportSheet[] {
	const tables = Array.from(container.querySelectorAll('table')).filter((table) =>
		!table.parentElement?.closest('table')
		&& !table.closest('[data-export-ignore]')
		&& !isHidden(table)
	);

	return tables
		.map((table, index) => {
			const title = tableTitle(table, container);
			return { name: title || (tables.length > 1 ? `Table ${index + 1}` : 'Report'), title, ...tableRows(table) };
		})
		.filter((sheet) => sheet.rows.length > 0);
}

/** Download the report's tables as an .xlsx made by the server; throws with the reason when it cannot. */
export async function downloadTablesAsExcel(container: HTMLElement, options: ExcelExportOptions): Promise<void> {
	const sheets = [...(options.extraSheets || []), ...readTables(container)];
	if (sheets.length === 0) {
		throw new Error('There is no report table to download. View the report first.');
	}

	const response = await fetch(
		process.env.REACT_APP_API_URL + 'reports/excel-export',
		postRequestOptions({ title: options.title, filename: options.filename, meta: options.meta || [], sheets })
	);
	if (!response.ok) {
		let message = 'The Excel file could not be made.';
		try {
			const body = await response.json();
			message = body.errorMessage || body.message || message;
		} catch (error) {
			// not JSON: keep the general message
		}
		throw new Error(message);
	}

	const blob = await response.blob();
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = `${options.filename || 'report'}.xlsx`;
	document.body.appendChild(link);
	link.click();
	link.remove();
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}
